import React, { useState, useEffect } from "react";
import {
  User,
  ArrowLeft,
  Mail,
  Calendar,
  Shield,
  Settings,
  Trash2,
  Crown,
  ArrowRight,
  Check,
} from "lucide-react";
import { onAuthStateChanged, deleteUser } from "firebase/auth";
import { deleteDoc, doc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { getUserData } from "../../services/userService";
import {
  getUserSubscription,
  getSubscriptionTier,
} from "../../services/subscriptionService";
import { useToast } from "../../components/Toast";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

const ProfilePage = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const functions = getFunctions();
  const [user, setUser] = useState({
    displayName: "-",
    email: "-",
    memberSince: "-",
    lastLogin: "-",
    accountType: "-",
    subscriptionStatus: "-",
    provider: "-",
    planName: "Starter",
  });
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);

  const handleManageSubscription = async () => {
    setLoadingPortal(true);

    try {
      const createPortalSessionFn = httpsCallable(
        functions,
        "createPortalSession"
      );
      const result = await createPortalSessionFn();
      const data = result.data;

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Failed to get portal URL");
      }
    } catch (err) {
      console.error("Error opening customer portal:", err);
      addToast(
        err?.message ||
          "Failed to open subscription management. Please try again.",
        "error"
      );
    } finally {
      setLoadingPortal(false);
    }
  };

  // Refresh subscription status when page becomes visible (e.g., returning from Stripe portal)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refresh subscription status
        const refreshStatus = async () => {
          try {
            const user = auth.currentUser;
            if (user) {
              const plan = await getSubscriptionTier(user.uid);
              const subStatus = await getUserSubscription(user.uid);
              setIsPro(subStatus === "active");

              // Update user state
              const profileDoc = await getUserData(user.uid);
              const planName = plan || "Starter";
              setUser((prev) => ({
                ...prev,
                planName: planName,
                subscriptionStatus:
                  subStatus === "-"
                    ? "-"
                    : subStatus.charAt(0).toUpperCase() + subStatus.slice(1),
              }));
            }
          } catch (error) {
            console.error("Error refreshing subscription status:", error);
          }
        };
        refreshStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (!authUser) {
          setUser({
            displayName: "-",
            email: "-",
            memberSince: "-",
            lastLogin: "-",
            accountType: "-",
            subscriptionStatus: "-",
            provider: "-",
          });
          setLoading(false);
          return;
        }

        let profileDoc = null;
        try {
          profileDoc = await getUserData(authUser.uid);
        } catch (e) {
          // Swallow and continue with auth data only
        }

        const formatTimestamp = (ts) => {
          if (!ts) return "-";
          try {
            if (typeof ts.toDate === "function") {
              return ts.toDate().toLocaleString();
            }
            if (typeof ts === "number") {
              return new Date(ts).toLocaleString();
            }
            return new Date(ts).toLocaleString();
          } catch (_err) {
            return "-";
          }
        };

        const createdAtDisplay = profileDoc?.createdAt
          ? formatTimestamp(profileDoc.createdAt)
          : authUser?.metadata?.creationTime
          ? new Date(authUser.metadata.creationTime).toLocaleString()
          : "-";

        const lastLoginDisplay = profileDoc?.lastLoginAt
          ? formatTimestamp(profileDoc.lastLoginAt)
          : authUser?.metadata?.lastSignInTime
          ? new Date(authUser.metadata.lastSignInTime).toLocaleString()
          : "-";

        const providerId =
          authUser?.providerData?.[0]?.providerId ||
          profileDoc?.provider ||
          "-";
        const providerDisplay = providerId.includes("google")
          ? "Google"
          : providerId.includes("facebook")
          ? "Facebook"
          : providerId.includes("github")
          ? "GitHub"
          : providerId === "password"
          ? "Email"
          : providerId === "-"
          ? "-"
          : providerId;

        const subscription = profileDoc?.subscriptionStatus || "-";
        const subscriptionDisplay =
          subscription === "-"
            ? "-"
            : subscription.charAt(0).toUpperCase() + subscription.slice(1);

        const role = profileDoc?.role || "-";
        const accountTypeDisplay =
          role === "-" ? "-" : role.charAt(0).toUpperCase() + role.slice(1);

        // Get subscription plan name
        let planName = "Starter";
        let isProUser = false;
        try {
          if (authUser?.uid) {
            const plan = await getSubscriptionTier(authUser.uid);
            planName = plan || "Starter";
            const subStatus = await getUserSubscription(authUser.uid);
            isProUser = subStatus === "active";
          }
        } catch (error) {
          console.error("Error fetching subscription info:", error);
        }

        setIsPro(isProUser);

        setUser({
          displayName: profileDoc?.displayName || authUser.displayName || "-",
          email: profileDoc?.email || authUser.email || "-",
          memberSince: createdAtDisplay,
          lastLogin: lastLoginDisplay,
          accountType: accountTypeDisplay,
          subscriptionStatus: subscriptionDisplay,
          provider: providerDisplay,
          planName: planName,
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleBackToHome = () => {
    // This would typically use your router's navigation
    // For example: router.push('/') or navigate('/')
    // window.history.back();
    navigate("/home");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-x-hidden">
      {/* Background Gradients */}
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 opacity-20 blur-3xl"></div>
      <div className="absolute top-1/3 -left-40 w-96 h-96 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-20 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 opacity-15 blur-3xl"></div>
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 opacity-15 blur-3xl"></div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <button
            onClick={handleBackToHome}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mb-4 group"
          >
            <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Home</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            User Profile
          </h1>
          <p className="text-gray-600">
            Manage your account information and preferences
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8">
            <div className="flex items-center">
              <div className="h-20 w-20 rounded-full bg-white bg-opacity-20 flex items-center justify-center mr-6">
                <User className="h-10 w-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {user.displayName}
                </h2>
                <div className="flex items-center text-indigo-200">
                  <Shield className="h-4 w-4 mr-2" />
                  <span>Account Active</span>
                </div>
              </div>
            </div>
          </div>

          {/* User Details Grid */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Email */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="text-gray-900 font-medium">{user.email}</p>
                </div>
              </div>

              {/* Account Type */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Account Type
                </p>
                <p className="text-blue-600 font-semibold">
                  {user.accountType}
                </p>
              </div>

              {/* Member Since */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Member Since
                  </p>
                  <p className="text-gray-900 font-medium">
                    {user.memberSince}
                  </p>
                </div>
              </div>

              {/* Last Login */}
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Last Login
                </p>
                <p className="text-green-600 font-semibold">{user.lastLogin}</p>
              </div>

              {/* Subscription Status */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <Settings className="h-5 w-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Subscription Status
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p
                      className={`text-gray-900 font-medium ${
                        isPro ? "text-indigo-600" : ""
                      }`}
                    >
                      {user.planName}
                    </p>
                    {isPro && (
                      <>
                        <Crown className="h-4 w-4 text-indigo-600" />
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">
                          Active
                        </span>
                      </>
                    )}
                    {!isPro && (
                      <span className="text-xs text-gray-500">
                        (Free Forever)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Provider */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Provider
                </p>
                <p className="text-purple-600 font-semibold">{user.provider}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade Plan Section */}
        {!isPro && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 border border-gray-200">
            <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6 text-white relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
              ></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                    <Crown className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Upgrade to Pro</h3>
                    <p className="text-indigo-100 text-sm">
                      Unlock all premium features - £4.99/month
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/payment")}
                  className="bg-white text-indigo-600 font-semibold py-3 px-6 rounded-xl hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
                >
                  <span>Upgrade Now</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pro Plan Info Section */}
        {isPro && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 border border-gray-200">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 border border-indigo-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-indigo-100 rounded-lg p-2">
                  <Crown className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Pro Plan Active
                  </h3>
                  <p className="text-sm text-gray-600">
                    You have access to all premium features
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">12 Quiz Slots</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    25 AI Requests/day
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Full Vault Access
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Advanced Analytics
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Email Prizes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">
                    Priority Support
                  </span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-indigo-200">
                <button
                  onClick={handleManageSubscription}
                  disabled={loadingPortal}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>
                    {loadingPortal ? "Loading..." : "Manage Subscription"}
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4">
            <div className="flex items-center">
              <Trash2 className="h-6 w-6 text-white mr-3" />
              <h3 className="text-xl font-bold text-white">Danger Zone</h3>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Account
                </h4>
                <p className="text-gray-600">
                  Once you delete your account, there is no going back. Please
                  be certain.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete Account</span>
              </button>
            </div>
          </div>
        </div>
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => (deleting ? null : setShowDeleteModal(false))}
            />
            <div className="relative z-10 w-full max-w-md mx-4 rounded-2xl bg-white shadow-xl">
              <div className="px-6 py-5 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete account?
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  This action is permanent and cannot be undone.
                </p>
              </div>
              <div className="px-6 py-5 space-y-3">
                <p className="text-sm text-gray-700">
                  All your data may be removed. You may need to reauthenticate
                  to proceed.
                </p>
              </div>
              <div className="px-6 py-4 bg-gray-50 flex items-center justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  disabled={deleting}
                  onClick={async () => {
                    const currentUser = auth.currentUser;
                    if (!currentUser) {
                      addToast("No authenticated user found.", "error");
                      return;
                    }
                    setDeleting(true);
                    addToast("Deleting your account...", "info");
                    try {
                      try {
                        await deleteDoc(doc(db, "users", currentUser.uid));
                      } catch (_e) {}
                      await deleteUser(currentUser);
                      addToast("Account deleted successfully.", "success");
                      window.location.replace("/login");
                    } catch (e) {
                      if (e && e.code === "auth/requires-recent-login") {
                        addToast(
                          "Please reauthenticate to delete your account.",
                          "warning"
                        );
                        window.location.replace("/login");
                      } else {
                        addToast(
                          "Failed to delete account. Please try again.",
                          "error"
                        );
                      }
                    } finally {
                      setDeleting(false);
                      setShowDeleteModal(false);
                    }
                  }}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
